import Immutable from 'immutable';
import cloneDeep from 'lodash/cloneDeep';
import isEqual from 'lodash/isEqual';
import mapValues from 'lodash/mapValues';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import uuidv4 from 'uuid/v4';

import ErrorPage from 'app/components/ErrorPage';
import { MOCK_BROWSE_HOME_PAGE_INDEX_LOCAL_STORAGE_KEY } from 'app/constants/admin/browse/home';
import { HTTP_STATUS_CODE } from 'app/constants/ErrorConstants';
import { LOCALE_CODE } from 'app/constants/language';
import { GLOBAL_MESSAGING_ID, NO_OP_ACTION_TYPE } from 'app/constants/MessagingConstants';
import { hasAdminPermissions } from 'app/helpers/admin/browse/categories';
import { cleanBrowseHomePage } from 'app/helpers/admin/browse/home';
import { selectCurrentUser } from 'app/selectors/AuthSelector';
import AdminBrowseHomeEditPage from './AdminBrowseHomeEditPage';

// temporary API methods that call local storage
const fetchBrowseHomePage = browseHomePageId => {
  const browseHomePageIndex = JSON.parse(localStorage.getItem(MOCK_BROWSE_HOME_PAGE_INDEX_LOCAL_STORAGE_KEY)) || {};
  const cleanBrowseHomePageIndex = mapValues(browseHomePageIndex, cleanBrowseHomePage);

  return new Promise((resolve, reject) => {
    // fake api response latency
    setTimeout(() => {
      const browseHomePage = cleanBrowseHomePageIndex[browseHomePageId];

      if (browseHomePage) {
        resolve(browseHomePage);
      } else {
        reject(new Error(`No browseHomePage found with id ${browseHomePageId}`));
      }
    }, 1000);
  });
};

const upsertBrowseHomePage = browseHomePage => {
  const browseHomePageIndex = JSON.parse(localStorage.getItem(MOCK_BROWSE_HOME_PAGE_INDEX_LOCAL_STORAGE_KEY)) || {};

  browseHomePageIndex[browseHomePage.browseHomePageId] = browseHomePage;

  window.localStorage.setItem(MOCK_BROWSE_HOME_PAGE_INDEX_LOCAL_STORAGE_KEY, JSON.stringify(browseHomePageIndex));

  return new Promise(resolve => {
    setTimeout(() => {
      resolve(browseHomePage);
    }, 1000);
  });
};

const successMessageAction = {
  messaging: Immutable.fromJS({
    [GLOBAL_MESSAGING_ID]: {
      message: Immutable.Map({
        defaultMessage: 'Saved',
        id: GLOBAL_MESSAGING_ID,
        type: 'Success'
      }),
      duration: 5000
    }
  }),
  type: NO_OP_ACTION_TYPE
};

const AdminBrowseHomeEditPageContainer = ({ browseHomePageId }) => {
  const dispatch = useDispatch();

  const currentUser = useSelector(selectCurrentUser);

  const [state, setState] = useState({
    browseHomePage: null,
    browseHomePageWorkingCopy: null,
    hasUnsavedChanges: false,
    isFetching: true,
    isSaving: false
  });

  const { browseHomePage, browseHomePageWorkingCopy, hasUnsavedChanges, isFetching, isSaving } = state;

  useEffect(() => {
    Promise.resolve()
      .then(() => {
        if (browseHomePageId) {
          return fetchBrowseHomePage(browseHomePageId);
        }

        return Promise.resolve({
          browseHomePageId: uuidv4(),
          createdBy: currentUser.get('name'),
          isActive: false,
          localeCode: LOCALE_CODE.UNITED_STATES_ENGLISH, // TODO: we will have to get this from page params
          name: 'Untitled Browse Home Page',
          sections: [],
          updatedAt: null,
          updatedBy: null
        });
      })
      .then(fetchedBrowseHomePage => {
        setState(prevState => {
          return {
            ...prevState,
            browseHomePage: fetchedBrowseHomePage,
            browseHomePageWorkingCopy: cloneDeep(fetchedBrowseHomePage),
            isFetching: false
          };
        });
      })
      .catch(err => {
        // TODO: handle with toast notification
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      });
  }, [browseHomePageId, currentUser]);

  const canViewPage = hasAdminPermissions(currentUser);
  if (!canViewPage) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.FORBIDDEN} />;
  }

  const handleWorkingCopyChange = newBrowseHomePageWorkingCopy => {
    const newHasUnsavedChanges = !isEqual(newBrowseHomePageWorkingCopy, browseHomePage);

    setState(prevState => {
      return {
        ...prevState,
        browseHomePageWorkingCopy: newBrowseHomePageWorkingCopy,
        hasUnsavedChanges: newHasUnsavedChanges
      };
    });
  };

  const saveBrowseHomePage = () => {
    browseHomePageWorkingCopy.updatedAt = Date.now();
    browseHomePageWorkingCopy.updatedBy = currentUser.get('name');

    setState(prevState => {
      return {
        ...prevState,
        hasUnsavedChanges: false,
        isSaving: true
      };
    });

    upsertBrowseHomePage(browseHomePageWorkingCopy)
      .then(savedBrowseHomePage => {
        dispatch(successMessageAction);

        setState(prevState => {
          return {
            ...prevState,
            browseHomePage: savedBrowseHomePage,
            browseHomePageWorkingCopy: cloneDeep(savedBrowseHomePage)
          };
        });
      })
      .catch(err => {
        // TODO: handle save error
        /* eslint-disable-next-line no-console */
        console.error(err.message);
      })
      .finally(() => {
        setState(prevState => {
          return {
            ...prevState,
            isSaving: false
          };
        });
      });
  };

  if (isFetching) {
    return <p>Loading...</p>;
  }
  if (!browseHomePage) {
    return <ErrorPage httpStatusCode={HTTP_STATUS_CODE.NOT_FOUND} />;
  }

  return (
    <AdminBrowseHomeEditPage
      browseHomePage={browseHomePageWorkingCopy}
      hasUnsavedChanges={hasUnsavedChanges}
      isSaving={isSaving}
      onChange={handleWorkingCopyChange}
      onSave={saveBrowseHomePage}
    />
  );
};

AdminBrowseHomeEditPageContainer.propTypes = {
  browseHomePageId: PropTypes.string
};

export default AdminBrowseHomeEditPageContainer;
